import React from 'react';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { EmptyArt } from '@/components/EmptyIllustrations';
import { FirstRunChecklist } from '@/components/FirstRunChecklist';

const C = { navy: '#0D1B2A', teal: '#00B4D8', green: '#1D9E75', slate: '#5C6B7A', bg: '#F5F7FA', blue: '#1A4C7C', saffron: '#FF9933' };

const EMPTY_CONFIGS = {
  dashboard: {
    art: 'dashboard',
    overline: 'Ready for launch',
    title: 'Welcome to frAIme',
    message: 'Your MGA platform is ready. Start by searching for a business entity and submitting your first risk.',
    cta: { label: 'Search Entities', path: '/search' },
    tips: ['Search 302,000+ pre-enriched AU/NZ businesses', 'Submit a risk to see AI underwriting in action', 'Your first policy will appear here automatically'],
  },
  submissions: {
    art: 'submissions',
    overline: 'ARIA is standing by',
    title: 'No submissions yet',
    message: 'Submissions are created when a broker or underwriter submits a risk for assessment. Start by searching for an entity.',
    cta: { label: 'Submit a Risk', path: '/search' },
    tips: ['Search for a business by ABN or name', 'Complete the submission form', 'AI scores and decides in under 5 seconds'],
  },
  policies: {
    art: 'policies',
    overline: 'Your book starts here',
    title: 'No active policies',
    message: 'Policies are created when a submission is approved and bound. Submit your first risk to generate a policy.',
    cta: { label: 'Submit a Risk', path: '/search' },
    tips: ['Approved submissions can be bound into policies', 'Policy schedules are auto-generated', 'Renewals are tracked automatically'],
  },
  claims: {
    art: 'claims',
    overline: 'ARIA-CLM is watching',
    title: 'No claims lodged',
    message: 'Claims appear here when a First Notice of Loss (FNOL) is submitted. Your claims pipeline is ready.',
    cta: { label: 'Lodge an FNOL', path: '/claims' },
    tips: ['4 triage pathways: fast track, standard, complex, fraud', 'Under $10K auto-settles', 'CAT mode handles surge capacity'],
  },
  renewals: {
    art: 'renewals',
    overline: 'The cycle is automated',
    title: 'No renewals in pipeline',
    message: 'Renewal quotes are generated automatically for policies approaching expiry. Generate your first batch to see the pipeline.',
    cta: null,
    tips: ['Claims-based loading adjusts renewal premiums', 'One-click offer, accept, or decline', 'Retention rate tracked automatically'],
  },
  portfolio: {
    art: 'portfolio',
    overline: 'Bird\u2019s-eye view pending',
    title: 'Portfolio is empty',
    message: 'Your portfolio drill-down will show insurance classes, clients, and policies once you have active policies.',
    cta: { label: 'Submit a Risk', path: '/search' },
    tips: ['16 insurance classes supported', 'Drill from class → client → policy → claim', 'GWP and claims tracked per class'],
  },
  compliance: {
    art: 'compliance',
    overline: 'AIXIOM-C is monitoring',
    title: 'Compliance hub ready',
    message: 'All 15 compliance modules are active and monitoring. Items will appear here as your portfolio grows.',
    cta: null,
    tips: ['IDR 30-day complaint clock', 'APRA Form 701 auto-populated', 'Staff competency tracking'],
  },
  finance: {
    art: 'finance',
    overline: 'Trust-grade accounting',
    title: 'No financial activity yet',
    message: 'Premium trust, commissions, and reinsurance flows will appear here once policies are bound.',
    cta: { label: 'View Compliance', path: '/compliance' },
    tips: ['Premium waterfall: GWP → commission → stamp duty → net', 'Trust account reconciliation', 'Bordereaux generation'],
  },
  brokers: {
    art: 'clients',
    overline: 'Distribution network',
    title: 'No broker activity',
    message: 'Broker performance data will appear here as submissions and policies are created.',
    cta: { label: 'Invite Brokers', path: '/onboarding' },
    tips: ['Brokers ranked by GWP and loss ratio', 'Per-broker class breakdown', 'Dimension U tracks broker accuracy'],
  },
  clients: {
    art: 'clients',
    overline: 'Your client base',
    title: 'No clients yet',
    message: 'Clients appear here as policies are written against them. Add a prospect or write your first quote to get started.',
    cta: null,
    tips: null,
  },
  complaints: {
    art: 'complaints',
    overline: 'RG 271 compliant',
    title: 'No complaints recorded',
    message: 'A clean register — when a complaint is lodged it lands here with a 30-day IDR clock, acknowledgement tracking and a full timeline.',
    cta: null,
    tips: null,
  },
  referrals: {
    art: 'referrals',
    overline: 'ARIA has this covered',
    title: 'Referral queue clear',
    message: 'Routine risks are scored, rated and bound autonomously. A submission lands here only when it breaches authority, scores in the grey zone, or trips a referral rule.',
    cta: null,
    tips: null,
  },
  search: {
    art: 'search',
    overline: 'Nothing matched',
    title: 'No results found',
    message: 'Try a different name or ABN — or create the business if it isn\u2019t on the register yet.',
    cta: null,
    tips: null,
  },
};

export default function EmptyState({ type, compact = false, className = '' }) {
  const navigate = useNavigate();
  const config = EMPTY_CONFIGS[type] || EMPTY_CONFIGS.dashboard;

  if (compact) {
    return (
      <div className={`py-8 px-6 text-center ${className}`} data-testid={`empty-state-${type}`}>
        <div className="mx-auto mb-2 flex justify-center"><EmptyArt type={config.art} width={130} /></div>
        <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{config.overline}</p>
        <p className="text-sm font-medium" style={{ color: C.navy }}>{config.title}</p>
        <p className="text-xs mt-1 max-w-sm mx-auto leading-relaxed" style={{ color: C.slate }}>{config.message}</p>
      </div>
    );
  }

  return (
    <Card className={`rounded-sm border border-[rgba(136,150,168,0.15)] bg-white ${className}`} data-testid={`empty-state-${type}`}>
      <div className="py-10 px-8 text-center max-w-lg mx-auto">
        <div className="mx-auto mb-4 flex justify-center"><EmptyArt type={config.art} width={190} /></div>

        <p className="text-[10px] uppercase tracking-[0.15em] mb-1.5" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{config.overline}</p>
        <h3 className="text-lg font-semibold mb-2" style={{ color: C.navy }}>{config.title}</h3>
        <p className="text-sm leading-relaxed" style={{ color: C.slate }}>{config.message}</p>

        {config.cta && (
          <button onClick={() => navigate(config.cta.path)}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: C.blue }}
            data-testid={`empty-state-cta-${type}`}>
            {config.cta.label}
            <ArrowRight className="w-4 h-4" />
          </button>
        )}

        {config.tips && (
          <div className="mt-6 pt-5 border-t border-[rgba(136,150,168,0.12)]">
            {['dashboard', 'submissions', 'policies'].includes(type) ? (
              <FirstRunChecklist embedded />
            ) : (
              <>
                <p className="text-[10px] uppercase tracking-wider font-medium mb-3" style={{ color: C.slate }}>Getting Started</p>
                <div className="space-y-2 text-left">
                  {config.tips.map(tip => (
                    <div key={tip} className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: C.teal }} />
                      <span className="text-xs" style={{ color: C.slate }}>{tip}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export { EMPTY_CONFIGS };
